import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Wallet, Users, FileText, X } from "lucide-react";

/**
 * Shown once for fresh tenants who have no data yet.
 * Suggests onboarding steps. Dismissible.
 */
export default function OnboardingModal() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dismissKey = "ac_onboarding_dismissed";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (localStorage.getItem(dismissKey) === "true") return;
      try {
        const [a, c, i] = await Promise.all([
          api.get("/accounts"),
          api.get("/customers"),
          api.get("/incoming-invoices"),
        ]);
        if (cancelled) return;
        const isFresh = a.data.length === 0 && c.data.length === 0 && i.data.length === 0;
        if (isFresh) setOpen(true);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const close = () => {
    localStorage.setItem(dismissKey, "true");
    setOpen(false);
  };

  const go = (to) => { close(); navigate(to); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-lg" data-testid="onboarding-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-500" /> Willkommen bei AgencyCash
            </DialogTitle>
          </div>
        </DialogHeader>
        <p className="text-neutral-600 text-sm">
          Damit AgencyCash dir den „Frei verfügbaren Betrag" zeigen kann, brauchen wir nur 3 schnelle Inputs:
        </p>
        <div className="space-y-3 pt-2">
          <Step n={1} icon={Wallet} title="Erstes Konto anlegen" desc="Geschäftskonto + aktueller Kontostand — kein Banking-Zugang nötig." cta="Konto anlegen" onClick={() => go("/konten")} testid="onboarding-step-account" />
          <Step n={2} icon={Users} title="1-2 Kunden erfassen" desc="Damit du Rechnungen und Projekte zuordnen kannst." cta="Kunden anlegen" onClick={() => go("/kunden")} testid="onboarding-step-customers" />
          <Step n={3} icon={FileText} title="Erste Rechnung erfassen" desc="Manuell, per Upload (PDF/OCR) oder per E-Mail-Forward." cta="Rechnung anlegen" onClick={() => go("/ausgangsrechnungen")} testid="onboarding-step-invoice" />
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-neutral-200 mt-4">
          <Button variant="ghost" size="sm" onClick={close} data-testid="onboarding-skip">
            <X className="h-3 w-3 mr-1" /> Später erinnern
          </Button>
          <p className="text-xs text-neutral-400">Tipp: Mit Demo-Daten registriert? Klick einfach durch.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Step({ n, icon: Icon, title, desc, cta, onClick, testid }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-neutral-200 hover:border-emerald-300 hover:bg-emerald-50/30 p-4 transition-colors cursor-pointer group" onClick={onClick} data-testid={testid}>
      <div className="h-8 w-8 rounded-lg bg-neutral-900 text-white flex items-center justify-center font-display font-semibold text-sm shrink-0">{n}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-neutral-700" />
          <div className="font-display font-semibold">{title}</div>
        </div>
        <div className="text-sm text-neutral-500 mt-0.5">{desc}</div>
      </div>
      <Button size="sm" variant="outline" className="shrink-0 group-hover:bg-emerald-500 group-hover:text-white group-hover:border-emerald-500">{cta} →</Button>
    </div>
  );
}
